<script lang="ts">
    // Svelte/Jet/Models/Types
    import { onMount, createEventDispatcher } from 'svelte';
    import { getJetPerform } from '~/jet';
    import type {
        // Artwork as ArtworkType,
        // ContentDescriptor,
        TrackLockup,
        TrackLockupLayoutKind,
    } from '~/jet/models';
    import { ContentKind, makeUserComponentsAction } from '~/jet/models';

    // Stores
    import { getI18n } from '~/stores/i18n';
    import { getItemSelector } from '~/stores/item-selector';

    // Constants/Assets
    import TrackVideoIcon from '~assets/icons/track_video.svg';

    // Components
    import Artwork from '~/components/jet/Artwork/Artwork.svelte';
    import ClickAction from '~/components/jet/action/ClickAction.svelte';
    import ExplicitBadge from '~/components/jet/ExplicitBadge.svelte';
    import InteractivePlayButton from '~/components/jet/buttons/InteractivePlayButton.svelte';
    import ProductContextMenu from '~/components/jet/ProductContextMenu.svelte';

    // Shared Components
    import SeparatedList from '@amp/web-app-components/src/components/helpers/SeparatedList.svelte';
    import TrackListItemLockup from '@amp/web-app-components/src/components/TrackList/TrackListItemLockup.svelte';

    // Utilities
    import { formatDuration, formatISODuration } from '~/utils/datetime';
    import { getCommerce } from '~/utils/commerce';
    import { getLocale } from '~/utils/locale';
    import { getPlayback } from '~/utils/playback';
    import { isPremiumSubscriber } from '~/utils/user';
    import { toggleFavorite } from '~/utils/favorites';
    import {
        makePausePlaybackAction,
        makeResumePlaybackAction,
    } from '~/actions/playback-actions';

    // Shared Utilities
    import allowDrop, {
        DropTarget,
    } from '@amp/web-app-components/src/actions/allow-drop';
    import { separatedList } from '@amp/web-app-components/src/utils/separated-list';
    import { TEXT_DIRECTION } from '@amp/web-app-components/src/constants';
    import AddToLibrary from '@amp/web-app-components/src/components/buttons/AddToLibrary.svelte';
    import {
        subscribeToId,
        type LibraryStateValue,
    } from '~/stores/local-library-state';

    // Imports not used for now but may be needed/need to be created later (or Music-equivalent thereof)
    //
    // import {
    //     getContentId,
    //     getContentIdForLibraryState,
    // } from '~/utils/content-descriptor';
    // import { getSongCanonicalURL } from '@jet-app/music/utilities/seo/Song';
    // import { addContainerItemsToPlayAction } from '~/actions/playback-actions';
    // import { getLocalLibraryState } from '~/utils/local-library-state';
    // import { isFavoritesPlaylist } from '~/stores/favorites-playlist';
    // import { flowActionForDestination } from '~/jet/models';
    // import { makeSongDetailPageIntent } from '~/jet/intents/song/SongDetailPageIntent';
    // import { FlowActionDestinationKind } from '@jet-app/music/action-model/FlowAction';
    // import { VIDEO_KINDS } from '~/constants/media-types';
    // import ArtworkWithBadge from '~/components/jet/Artwork/ArtworkWithBadge.svelte';
    // import BadgeArtwork from '~/components/jet/ProfileBadge/BadgeArtwork.svelte';
    // import FavoriteOrPopular from '~/components/jet/buttons/FavoriteOrPopular.svelte';
    // import AddToLibrary from '~/components/jet/buttons/AddToLibrary.svelte';
    // import CloudButtons from '~/components/jet/buttons/CloudButtons.svelte';

    // const localLibraryState = getLocalLibraryState();
    const localLibraryState = undefined;
    const i18n = getI18n();
    const itemSelector = getItemSelector();
    const locale = getLocale();
    const perform = getJetPerform();
    const { queue, isRestricted: getIsRestricted } = getPlayback();
    const { state: commerceState } = getCommerce();
    const dispatch = createEventDispatcher();

    let isRestricted: boolean = false;
    let isRTL: boolean = false;

    // TODO: Investigate why isRestricted is not getting set -- checking to see if this needs to be supported or not
    onMount(async () => {
        try {
            const restricted = await getIsRestricted?.();
            isRestricted = restricted;
        } catch {
            // noop
        }

        isRTL = document.dir === TEXT_DIRECTION.RTL;
    });

    // onDestroy(() => {
    //     localLibraryState.unsubscribeFromUpdates(libraryStateId);
    // });

    export let item: TrackLockup;
    // export let containerItemDescriptors: ContentDescriptor[] = [];
    export let rowIndex: number;
    export let isFirst: boolean;
    export let isLast: boolean;
    export let isSelected: boolean;
    export let isLastSelected: boolean;
    export let showByline: boolean;
    export let showSecondColumn: boolean;
    export let showThirdColumn: boolean;
    export let showFourthColumn: boolean;
    export let hasReactions: boolean = false;
    export let dropEnabled: boolean = false;
    // export let shouldShowRemoveFromPlaylist: boolean = false;
    // export let containerArtwork: Artwork;

    let showAddingToLibrarySpinner: boolean = false;
    let libraryState: LibraryStateValue;

    $: {
        subscribeToId(item.id?.split('-')?.reverse()?.[0].trim(), (value) => {
            libraryState = value;
            showAddingToLibrarySpinner = false;
        });
    }
    $: inLibrary = libraryState?.inLibrary;

    $: layoutKind = ((): TrackLockupLayoutKind => {
        const layoutStyle = item.layoutStyle;
        return typeof layoutStyle === 'string'
            ? layoutStyle
            : layoutStyle?.kind;
    })();

    $: isPremiumUser = isPremiumSubscriber(
        $commerceState?.user?.subscriberType,
    );
    // $: itemKind = item.contentDescriptor?.kind;

    // NOTE: TrackListItem is used by catalog and library albums/playlists.
    //       Playlists may contain "library-songs" that aren't actually in
    //       the users' library (ex: subscribed playlists).  Because of this,
    //       for albums/playlists, we check for library/favorites status based
    //       on the catalog ID, with the library ID as a fallback if a catalog ID
    //       doesn't exist on the content descriptor.  Because of this, we use a
    //       potentially different ID for managing library/favorites state.
    // $: itemId = getContentId(item);
    $: itemId = item?.id;
    // $: libraryStateId = getContentIdForLibraryState(item);
    $: libraryStateId = undefined;

    // $: isVideoKind = VIDEO_KINDS.includes(itemKind);
    $: isVideoKind = false;
    $: isPreview = !isPremiumUser;
    $: isExplicit = item.showExplicitBadge;
    $: isDisabled = item.isDisabled || (isExplicit && isRestricted);
    $: trackNumber = item.trackNumber;
    $: title = item.title;
    $: rankingText = item.rankingText;
    $: artwork = item.artwork;

    /**
     * If music videos are supported, use 'music-video-tracklist'
     * If social badges are supported, use 'track-list-decorated'
     * For now, only supporting 'track-list'
     */
    $: getArtworkProfile = () => {
        return 'track-list';
    };
    $: showArtwork = !!artwork && layoutKind === 'playlistTrackList';
    // $: showFavoriteButton = isPremiumUser && !isDisabled;
    $: showFavoriteButton = false;
    // $: trackUrl = item.contentDescriptor?.url;

    // $: songContentDescriptor = {
    //     ...item.contentDescriptor,
    //     url: getSongCanonicalURL(trackUrl),
    // };
    $: songContentDescriptor = undefined;
    // $: songSegue =
    //     !!songContentDescriptor && !isDisabled && generateSongSegue();
    // function generateSongSegue() {
    //     const intent = makeSongDetailPageIntent(songContentDescriptor);
    //     // Some library tracks may not contain a url, in which case we do not want
    //     // to generate a page intent as it will error when you try to click the link.
    //     // In some cases the url/catalogUrl property may not exist, and in other cases
    //     // it may exist but have a value of undefined.
    //     if (
    //         !!intent.contentDescriptor.url ||
    //         !!intent.contentDescriptor.catalogUrl
    //     ) {
    //         return flowActionForDestination(
    //             {
    //                 kind: FlowActionDestinationKind.CatalogPage,
    //                 intent: makeSongDetailPageIntent(songContentDescriptor),
    //             } as any,
    //             {
    //                 data: [
    //                     {
    //                         excludingFields: [],
    //                         includingFields: ['pageFields', 'languages'],
    //                         shouldFlush: false,
    //                         fields: {
    //                             targetId:
    //                                 songContentDescriptor?.identifiers
    //                                     .storeAdamID,
    //                             actionType: 'credits',
    //                             targetType: 'button',
    //                             actionContext: 'track',
    //                             eventType: 'click',
    //                         },
    //                     },
    //                 ],
    //                 custom: {},
    //             },
    //         );
    //     }
    // }
    $: songSegue = undefined;

    // Subtitles are displayed as bylines directly below track title, and used for AX label
    $: bylineData = item.subtitleLinks || [{ title: item.subtitle }];

    $: durationInSeconds = Math.trunc(item.duration / 1000);
    $: formattedDuration = formatDuration(durationInSeconds, locale.language);
    $: isoDuration = formatISODuration(durationInSeconds);

    // Playback
    $: isNowPlayingItem = itemId && itemId.includes($queue.nowPlayingItemId);
    $: isLoading = isNowPlayingItem && $queue.derivedStates?.isLoading;
    $: isPlaying = isNowPlayingItem && $queue.derivedStates?.isPlaying;
    $: isPaused = isNowPlayingItem && $queue.derivedStates?.isPaused;

    $: itemWithUpdatedPlaybackActions = ((): TrackLockup => {
        return {
            ...item,
            // TODO: rdar://141157893 ([Classical on Web] Playlist playback plays incorrect song)
            // The playAction (either directly or as a subaction on a compoundAction)
            // also needs to have containerItems to populate the queue for continuous playback
            playAction: !isDisabled && item.playAction,

            pauseAction: makePausePlaybackAction(),
            resumeAction: makeResumePlaybackAction(),
        };
    })();

    let rowElement: HTMLElement;

    // $: updatedState = localLibraryState.subscribeToUpdates(libraryStateId);
    $: trackContentKind = isVideoKind
        ? ContentKind.MusicVideo
        : ContentKind.Song;
    // $: inFavorites =
    //     $updatedState &&
    //     localLibraryState.get(libraryStateId, trackContentKind)?.inFavorites;
    $: inFavorites = false;

    const handleToggleFavorite = (): void => {
        // // contentDescriptor passed this way because of TS linitng errors:
        // // Error: Argument of type 'TrackLockup' is not assignable to parameter of type 'ItemWithContentDescriptor'.
        // // Property 'contentDescriptor' is optional in type 'TrackLockup' but required in type 'ItemWithContentDescriptor'. (ts)
        // toggleFavorite(
        //     libraryStateId,
        //     trackContentKind,
        //     item.contentDescriptor,
        //     inFavorites,
        //     localLibraryState,
        //     // $isFavoritesPlaylist,
        //     true,
        //     perform,
        // );
    };

    // Use an ARIA label to make screenreader announcements less verbose.
    // Without the label, "Favorite" (even when a track is not favorited),
    // "Add to Library", and "More" are announced on every row.
    $: ariaLabel = (() => {
        let label = title;

        // Add byline data (e.g. song artist on a playlist) to AX label,
        // if present.
        if (bylineData.length) {
            label = $i18n.t('ASE.Web.MusicClassical.ContentA.Comma.ContentB', {
                contentA: title,
                contentB: separatedList(
                    bylineData.map((item) => item.title),
                    locale.language,
                    $i18n.t,
                ),
            });
        }

        if (isExplicit) {
            label = $i18n.t('ASE.Web.MusicClassical.ContentA.Comma.ContentB', {
                contentA: $i18n.t('ASE.Web.MusicClassical.Explicit'),
                contentB: label,
            });
        }

        return label;
    })();

    function performPlayAction() {
        if (item.playAction && !isDisabled) {
            perform(itemWithUpdatedPlaybackActions.playAction);
        }
    }
</script>

<div
    class="songs-list-row-container"
    class:show-library-spinner={showAddingToLibrarySpinner}
>
    <TrackListItemLockup
        {item}
        {layoutKind}
        {rowIndex}
        {isFirst}
        {isLast}
        {isSelected}
        {isLastSelected}
        {showByline}
        {showSecondColumn}
        {showThirdColumn}
        {showFourthColumn}
        {ariaLabel}
        {showArtwork}
        hasSocialBadging={false}
        {hasReactions}
        {dropEnabled}
        {isDisabled}
        {isLoading}
        {isPlaying}
        {isPaused}
        {isPreview}
        itemSelectorFn={itemSelector}
        on:keyDownPlayAction={performPlayAction}
        on:trackListItemDoubleClick={performPlayAction}
    >
        <!-- TODO: slot 'favorite-or-popular' -->
        <!-- rdar://137428278 (Web Classical Music: Album/Playlist Detail Pages - Track/Work Favoriting) -->

        <!-- Pre column: for rendering play states outside of track lists -->
        <svelte:fragment slot="prefixed-play-states">
            {#if item.playAction && !isDisabled}
                <InteractivePlayButton
                    ariaLabel={$i18n.t(
                        'ASE.Web.MusicClassical.Track.Play.AriaLabel',
                        {
                            songName: title,
                            artistName: item.artistName,
                        },
                    )}
                    item={itemWithUpdatedPlaybackActions}
                    {isExplicit}
                />
            {/if}
        </svelte:fragment>

        <svelte:fragment slot="artwork-with-badge">
            {#if showArtwork}
                <Artwork
                    {artwork}
                    profile={getArtworkProfile()}
                    fallbackKind={item.type}
                    alt={title}
                />
            {/if}
        </svelte:fragment>

        <svelte:fragment slot="song-click-action">
            {#if isExplicit}
                <div class="songs-list-row__explicit-wrapper" dir="auto">
                    <ClickAction action={songSegue}>
                        <!-- svelte-ignore a11y-role-has-required-aria-props -->
                        <div
                            class="songs-list-row__song-name"
                            aria-label={title}
                            tabindex="-1"
                            role="checkbox"
                            dir="auto"
                            data-testid="track-title"
                        >
                            {title}
                        </div>
                    </ClickAction>
                    <span
                        class="songs-list-row__badge songs-list-row__badge--explicit"
                    >
                        <ExplicitBadge />
                    </span>
                </div>
            {:else}
                <ClickAction action={songSegue}>
                    <!-- svelte-ignore a11y-role-has-required-aria-props -->
                    <div
                        class="songs-list-row__song-name"
                        aria-label={title}
                        tabindex="-1"
                        role="checkbox"
                        dir="auto"
                        data-testid="track-title"
                    >
                        {title}
                    </div>
                </ClickAction>
            {/if}
        </svelte:fragment>

        <svelte:fragment slot="first-column-byline">
            {#if bylineData.length && showByline}
                <span>
                    <SeparatedList
                        items={bylineData}
                        translateFn={$i18n.t}
                        let:item={byLineItem}
                    >
                        <!-- Use tabindex="-1" for links in songs-list-row to prevent focus on row when clicking link -->
                        <ClickAction action={byLineItem.segue}
                            >{byLineItem.title}</ClickAction
                        >
                    </SeparatedList>
                </span>
            {/if}
        </svelte:fragment>

        <!-- last column: duration + controls (library, contextual menu, preview button) -->
        <svelte:fragment slot="last-column-data">
            {#if isPreview}
                <div
                    class="songs-list-row__preview-button"
                    data-testid="track-preview"
                >
                    <ClickAction action={item.playAction}>
                        {$i18n.t('ASE.Web.MusicClassical.Preview.AllCaps')}
                    </ClickAction>
                </div>
            {/if}
            {#if (!isPreview || !isDisabled) && libraryState && !inLibrary}
                <div class="songs-list-row__add-to-library">
                    <AddToLibrary
                        translateFn={$i18n.t}
                        iconOnly={true}
                        showSpinner={showAddingToLibrarySpinner}
                        showAddToLibraryButton={!inLibrary}
                        on:addClick={() => {
                            showAddingToLibrarySpinner = true;
                            perform(
                                makeUserComponentsAction(
                                    `/query/command/library/${locale.storefront}`,
                                    'perform',
                                    {
                                        id: libraryState?.id,
                                        entityType: libraryState?.type,
                                        inLibrary: !inLibrary,
                                    },
                                ),
                            );
                        }}
                    />
                </div>
            {/if}
            {#if !isDisabled}
                <time
                    datetime={isoDuration}
                    class="songs-list-row__length"
                    data-testid="track-duration"
                >
                    {formattedDuration}
                </time>

                <div class="songs-list-row__context-menu">
                    <ProductContextMenu
                        contextMenuAction={item.contextMenuAction}
                        hasMaterial={false}
                        buttonStyle="non-platter"
                    />
                </div>
            {/if}
        </svelte:fragment>
    </TrackListItemLockup>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '@amp/web-shared-styles/app/core/mixins/tabular-numbers' as *;

    .songs-list-row-container {
        // We cannot use `display: contents` here, as this will break the AX tree for VO
        // and cause the entire track list to become inaccessible.
        //
        // It is valid to wrap table rows in "row groups". This keeps the AX tree valid
        // so that VO parses it correctly, while also not upsetting the existing styles.
        display: table-row-group;

        .songs-list-row__preview-button,
        .songs-list-row__length {
            grid-area: song-controls-length;
        }

        .songs-list-row__length {
            @include tabular-numbers;
        }

        .songs-list-row__preview-button {
            font: var(--caption-1-emphasized);
            opacity: var(--playButtonOpacity, 0);
            z-index: var(--z-default);

            // In parity w/ Music the preview state is hidden when the viewport
            // is narrow. This may change with additional feedback from Design
            @media (--range-grid-layout-xs-1-sidebar-down) {
                display: none;
            }
        }

        .songs-list-row__add-to-library {
            grid-area: song-controls-add;
            opacity: var(--addToLibraryOpacity, 0);
            margin-inline-end: 7px;
        }

        .songs-list-row__preview-button + .songs-list-row__length {
            opacity: calc(1 - var(--playButtonOpacity, 0));

            // In parity w/ iOS Classical Music does not hide the duration
            // when the preview state is is present & the viewport is small
            @media (--range-grid-layout-xs-1-sidebar-down) {
                // TODO rename custom prop to be more generic via
                // shared component TrackListItemLockup
                --playButtonOpacity: 0;
            }
        }

        .songs-list-row__context-menu {
            grid-area: song-controls-context;
        }

        .songs-list-row__length + .songs-list-row__context-menu {
            margin-inline-start: $songs-list-row-context-menu-spacer;
        }

        :global(
                .songs-list-row:not(
                        .songs-list-row--selected,
                        .songs-list-row--disabled
                    )
                    .songs-list-row__preview-button
            ) {
            color: var(--keyColor);
        }

        // Override line clamp from shared styles -- Classical does not truncate track list items.
        // TODO: rdar://89617186 (JMOTW: UXD: Tracklist: investigate how we can have shared styling between different files)
        :global(.songs-list__col.songs-list__col--song),
        :global(.songs-list-row__by-line) {
            white-space: normal;
        }

        :global(.songs-list-row__song-index) {
            // Align artwork to top of track title, rather than center of track row.
            // This should only take effect when title is great than two lines.
            align-self: start;
        }

        // Increased specificity to overcome shared `.songs-list-row--no-artwork` padding
        :global(.songs-list-row .songs-list-row__song-container) {
            padding-inline-start: 18px;

            // Adjustments after removing line clamp from song title
            // - Allow height to expand beyond normal track height
            // - Add padding above and below song title
            padding-block: 12px;
            min-height: $songs-list-row-height;
            height: auto;
        }

        :global(.artwork-component) {
            border-radius: 5px;
        }

        // Remove absolute positioning from playback bars to fix alignment
        // & interactivity when play states are prefixed outside of track list
        // TODO: Explore refactoring playback-bars styles in TrackListItemLockup shared component
        :global(.prefixed-play-states .playback-bars),
        :global(.prefixed-play-states .playback-bars__svg) {
            position: initial;
        }

        // Remove border radius from track items with work headers
        :global(.songs-list--has-works-header) & {
            :global(.songs-list__col:nth-child(2)),
            :global(.songs-list__col:last-child) {
                border-radius: 0;
            }

            // Keep border radius for last track item
            &:last-of-type {
                :global(.songs-list__col:nth-child(2)) {
                    border-end-start-radius: 6px;
                }

                :global(.songs-list__col:last-child) {
                    border-end-end-radius: 6px;
                }
            }
        }
    }

    .show-library-spinner {
        --addToLibraryOpacity: 1;
    }

    // Remove preview button and add to library from narrow
    // viewports to allow more space for song title before truncating.
    @media (--range-grid-layout-xs-1-sidebar-down) {
        .songs-list-row__preview-button,
        .songs-list-row__add-to-library {
            display: none;
        }
    }

    // Remove buttons that appear on hover on touchscreen devices.
    // (Actions are still available through the "more" contextual menu.)
    @media (--target-touchscreen) {
        .songs-list-row__preview-button,
        .songs-list-row__add-to-library {
            display: none;
        }
    }
</style>
